import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
    FaLock,
    FaShieldAlt,
    FaCreditCard,
    FaCheckCircle,
    FaFileContract,
    FaArrowLeft
} from "react-icons/fa";

export default function PaymentPage() {
    const [searchParams] = useSearchParams();
    const [ready, setReady] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const sessionId = searchParams.get("session");

    useEffect(() => {
        if (!sessionId) return;

        const script = document.createElement("script");

        script.src =
            "https://ap-gateway.mastercard.com/static/checkout/checkout.min.js";

        script.setAttribute("data-error", "errorCallback");
        script.setAttribute("data-cancel", "cancelCallback");

        script.onload = () => {
            setReady(true);
        };

        document.body.appendChild(script);

        window.errorCallback = function (error) {
            alert("Payment Error ❌");
            console.log(error);
        };

        window.cancelCallback = function () {
            alert("Payment Cancelled ❌");
        };

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }

            delete window.errorCallback;
            delete window.cancelCallback;
        };
    }, [sessionId]);

    const startPayment = () => {
        if (!accepted) {
            alert("⚠️ Please accept Terms & Conditions");
            return;
        }

        if (!window.Checkout || !sessionId) {
            alert("Payment not ready");
            return;
        }

        window.Checkout.configure({
            session: {
                id: sessionId
            }
        });

        window.Checkout.showPaymentPage();
    };

    return (
        <div className="min-h-screen bg-[#F3F8FF]">

            {/* =====================================================
                PAYMENT HERO
            ===================================================== */}

            <section className="relative overflow-hidden bg-[#002B45]">

                {/* Decorative Glow */}

                <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-[#174EA6]/30 blur-3xl" />

                <div className="pointer-events-none absolute -left-32 bottom-[-120px] h-80 w-80 rounded-full bg-[#FFC928]/10 blur-3xl" />

                {/* Decorative Circles */}

                <div className="pointer-events-none absolute right-[12%] top-1/2 h-40 w-40 -translate-y-1/2 rounded-full border border-white/5" />

                <div className="pointer-events-none absolute right-[14%] top-1/2 h-24 w-24 -translate-y-1/2 rounded-full border border-[#FFC928]/10" />

                <div className="relative z-10 mx-auto max-w-7xl px-4 pb-10 pt-28 sm:px-6 sm:pb-12 sm:pt-32 lg:px-8">

                    <div className="max-w-3xl">

                        {/* Badge */}

                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#FFC928] backdrop-blur-sm">

                            <FaLock className="text-xs" />

                            Secure Checkout

                        </div>

                        {/* Heading */}

                        <h1 className="mt-4 text-3xl font-extrabold tracking-tight !text-white sm:text-4xl lg:text-5xl">

                            Complete Your{" "}

                            <span className="text-[#FFC928]">
                                Payment
                            </span>

                        </h1>

                        {/* Description */}

                        <p className="mt-3 max-w-2xl text-sm leading-7 text-white/60 sm:text-base">

                            You're one step away from completing your booking.
                            Your payment is securely processed through
                            Mastercard Payment Gateway Services.

                        </p>

                        {/* Security Badges */}

                        <div className="mt-6 flex flex-wrap items-center gap-3">

                            <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold text-white/80 backdrop-blur-sm">

                                <FaShieldAlt className="text-[#FFC928]" />

                                Secure & Encrypted

                            </div>

                            <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold text-white/80 backdrop-blur-sm">

                                <FaCreditCard className="text-[#FFC928]" />

                                Mastercard Gateway

                            </div>

                        </div>

                    </div>

                </div>

                {/* Bottom Accent */}

                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

            </section>


            {/* =====================================================
                PAYMENT CONTENT
            ===================================================== */}

            <main className="relative overflow-hidden px-4 py-10 sm:px-6 sm:py-14 lg:px-8">

                {/* Background Decorations */}

                <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[#174EA6]/5 blur-3xl" />

                <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-[#FFC928]/5 blur-3xl" />


                <div className="relative z-10 mx-auto max-w-5xl">

                    {/* Back Link */}

                    <div className="mb-6">

                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-[#174EA6]"
                        >
                            <FaArrowLeft className="text-xs" />
                            Back to Home
                        </Link>

                    </div>


                    {/* =================================================
                        PAYMENT CARD
                    ================================================= */}

                    <div className="grid overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] lg:grid-cols-[0.85fr_1.15fr]">


                        {/* =================================================
                            LEFT INFORMATION PANEL
                        ================================================= */}

                        <div className="relative overflow-hidden bg-[#002B45] p-7 sm:p-10">

                            {/* Decorative Glow */}

                            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#174EA6]/30 blur-3xl" />

                            <div className="pointer-events-none absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-[#FFC928]/10 blur-3xl" />


                            <div className="relative z-10">

                                {/* Icon */}

                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#174EA6] text-2xl text-white shadow-lg shadow-[#174EA6]/20">

                                    <FaLock />

                                </div>


                                <h2 className="mt-7 text-2xl font-extrabold !text-white sm:text-3xl">

                                    Secure Payment

                                </h2>


                                <p className="mt-3 text-sm leading-7 text-white/60">

                                    Your payment information is protected and
                                    securely processed through Mastercard's
                                    payment infrastructure.

                                </p>


                                {/* Security Features */}

                                <div className="mt-8 space-y-4">

                                    <div className="flex items-start gap-3">

                                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#FFC928]">

                                            <FaShieldAlt />

                                        </div>

                                        <div>

                                            <p className="text-sm font-bold text-white">

                                                Secure & Encrypted

                                            </p>

                                            <p className="mt-1 text-xs leading-5 text-white/50">

                                                Your payment data is securely
                                                transmitted and protected.

                                            </p>

                                        </div>

                                    </div>


                                    <div className="flex items-start gap-3">

                                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#FFC928]">

                                            <FaCreditCard />

                                        </div>

                                        <div>

                                            <p className="text-sm font-bold text-white">

                                                Trusted Payment Gateway

                                            </p>

                                            <p className="mt-1 text-xs leading-5 text-white/50">

                                                Payments are processed through
                                                Mastercard Payment Gateway
                                                Services.

                                            </p>

                                        </div>

                                    </div>


                                    <div className="flex items-start gap-3">

                                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#FFC928]">

                                            <FaCheckCircle />

                                        </div>

                                        <div>

                                            <p className="text-sm font-bold text-white">

                                                Safe Checkout

                                            </p>

                                            <p className="mt-1 text-xs leading-5 text-white/50">

                                                Your transaction is handled
                                                securely from start to finish.

                                            </p>

                                        </div>

                                    </div>

                                </div>


                                {/* Security Notice */}

                                <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-4">

                                    <div className="flex items-start gap-3">

                                        <FaLock className="mt-0.5 shrink-0 text-[#FFC928]" />

                                        <p className="text-xs leading-5 text-white/50">

                                            We never store your complete card
                                            details on our website.

                                        </p>

                                    </div>

                                </div>

                            </div>

                        </div>


                        {/* =================================================
                            RIGHT PAYMENT ACTION PANEL
                        ================================================= */}

                        <div className="p-7 sm:p-10">

                            {/* Section Header */}

                            <div className="mb-7">

                                <div className="mb-2 flex items-center gap-2">

                                    <FaFileContract className="text-[#174EA6]" />

                                    <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#174EA6]">

                                        Payment Authorization

                                    </span>

                                </div>

                                <h2 className="text-2xl font-extrabold text-[#0F172A]">

                                    Ready to Pay?

                                </h2>

                                <p className="mt-2 text-sm leading-6 text-slate-500">

                                    Please review and accept our terms before
                                    continuing to the secure payment page.

                                </p>

                            </div>


                            {/* =================================================
                                AGREEMENT BOX
                            ================================================= */}

                            <div
                                className={`rounded-2xl border p-5 transition-all duration-300 ${accepted
                                    ? "border-[#174EA6]/30 bg-[#EAF3FF]"
                                    : "border-slate-200 bg-slate-50"
                                    }`}
                            >

                                <label className="flex cursor-pointer items-start gap-4">

                                    {/* Checkbox */}

                                    <div className="relative mt-0.5 shrink-0">

                                        <input
                                            type="checkbox"
                                            checked={accepted}
                                            onChange={() =>
                                                setAccepted(!accepted)
                                            }
                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-slate-300 bg-white transition-all checked:border-[#174EA6] checked:bg-[#174EA6]"
                                        />

                                        <FaCheckCircle
                                            className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[11px] text-white transition-opacity ${accepted
                                                ? "opacity-100"
                                                : "opacity-0"
                                                }`}
                                        />

                                    </div>


                                    {/* Text */}

                                    <div>

                                        <p className="text-sm font-semibold leading-6 text-[#0F172A]">

                                            I agree to the{" "}

                                            <Link
                                                to="/terms-conditions"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                className="font-bold text-[#174EA6] underline decoration-[#FFC928] decoration-2 underline-offset-2 transition hover:text-[#002B45]"
                                            >
                                                Terms & Conditions
                                            </Link>{" "}

                                            and{" "}

                                            <Link
                                                to="/privacy-policy"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                className="font-bold text-[#174EA6] underline decoration-[#FFC928] decoration-2 underline-offset-2 transition hover:text-[#002B45]"
                                            >
                                                Privacy Policy
                                            </Link>
                                            .
                                        </p>

                                        <p className="mt-2 text-xs leading-5 text-slate-500">

                                            Please read our policies carefully
                                            before proceeding with your
                                            payment.

                                        </p>

                                    </div>

                                </label>

                            </div>


                            {/* =================================================
                                PAYMENT STATUS
                            ================================================= */}

                            <div className="mt-6">

                                {!sessionId ? (

                                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4">

                                        <div className="flex items-start gap-3">

                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-500">

                                                !

                                            </div>

                                            <div>

                                                <p className="text-sm font-bold text-red-700">

                                                    Payment Session Missing

                                                </p>

                                                <p className="mt-1 text-xs leading-5 text-red-600">

                                                    We could not find a valid
                                                    payment session. Please
                                                    return to your booking and
                                                    try again.

                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                ) : !ready ? (

                                    <div className="rounded-2xl border border-[#174EA6]/15 bg-[#EAF3FF] p-4">

                                        <div className="flex items-center gap-3">

                                            {/* Spinner */}

                                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#174EA6]/20 border-t-[#174EA6]" />

                                            <div>

                                                <p className="text-sm font-bold text-[#002B45]">

                                                    Preparing Secure Checkout...

                                                </p>

                                                <p className="mt-1 text-xs text-slate-500">

                                                    Please wait while the secure
                                                    payment gateway loads.

                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                ) : (

                                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">

                                        <div className="flex items-center gap-3">

                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">

                                                <FaCheckCircle />

                                            </div>

                                            <div>

                                                <p className="text-sm font-bold text-emerald-700">

                                                    Secure Checkout Ready

                                                </p>

                                                <p className="mt-1 text-xs text-emerald-600">

                                                    Your secure payment session
                                                    is ready.

                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                )}

                            </div>


                            {/* =================================================
                                PAYMENT BUTTON
                            ================================================= */}

                            <button
                                onClick={startPayment}
                                disabled={!ready}
                                className={`mt-6 flex w-full items-center justify-center gap-3 rounded-xl px-6 py-4 text-sm font-extrabold text-white shadow-lg transition-all duration-300 ${ready
                                    ? "cursor-pointer bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928] hover:-translate-y-0.5 hover:shadow-xl"
                                    : "cursor-not-allowed bg-slate-300 shadow-none"
                                    }`}
                            >

                                {ready ? (
                                    <>
                                        <FaLock />

                                        Proceed to Secure Payment
                                    </>
                                ) : (
                                    <>
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                                        Preparing Secure Checkout...
                                    </>
                                )}

                            </button>


                            {/* =================================================
                                PAYMENT FOOTER
                            ================================================= */}

                            <div className="mt-6 border-t border-slate-100 pt-6">

                                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-medium text-slate-400">

                                    <span className="flex items-center gap-1.5">

                                        <FaLock className="text-[#174EA6]" />

                                        Secure Payment

                                    </span>

                                    <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:block" />

                                    <span>

                                        Mastercard Gateway

                                    </span>

                                    <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:block" />

                                    <span>

                                        Encrypted Checkout

                                    </span>

                                </div>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        BOTTOM SECURITY NOTE
                    ================================================= */}

                    <div className="mt-6 flex flex-col items-center justify-center gap-2 text-center sm:flex-row">

                        <FaShieldAlt className="text-[#174EA6]" />

                        <p className="text-xs leading-5 text-slate-500">

                            Your transaction is securely processed through
                            Mastercard Payment Gateway Services.

                        </p>

                    </div>

                </div>

            </main>

        </div>
    );
}